import type { Subscription } from "rxjs";
import { of as observableOf, Subject } from "rxjs";

import type { StateService } from "@uirouter/angularjs";
import type { IComponentController, IScope, ITimeoutService } from "angular";
import { noop } from "angular";
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, switchMap } from "rxjs/operators";

import type { IModalService } from "App/Modules/Services";
import type { ILoginService, IProfileService } from "App/Modules/Auth";

import type { ILogin, IProfile, IResponse, IResponseWithLoading, IToken } from "../../../Models";
import { TwoFactorAuthMode } from "../../../Models";
import { extendWithLoadingAndFail } from "../../../Services/ObservableExtensions";

export class LoginFormComponentController implements IComponentController {
    public static $inject = ["$scope", "$timeout", "mmLoginService", "$state", "mmProfileService", "mmModalService"];

    public login: ILogin = {
        password: null,
        twoFactorAuthCode: null,
        username: null,
    };

    public requiresTwoFactorAuth = false;

    public stateAfterLogin: string;

    public loginResponse: IResponseWithLoading<IToken>;

    public tfaResponse: IResponseWithLoading<TwoFactorAuthMode>;

    private profileSubscription: Subscription;

    private tfaSubscription: Subscription;

    private checkTfaSubject: Subject<string>;

    private isLoggedIn: boolean;

    private profile: IProfile;

    private beforeRedirect = false;

    constructor(
        private readonly $scope: IScope,
        private readonly $timeout: ITimeoutService,
        private readonly loginService: ILoginService,
        private readonly stateService: StateService,
        private readonly profileService: IProfileService,
        private readonly modalService: IModalService,
    ) {}

    public $onInit(): void {
        this.checkTfaSubject = new Subject<string>();

        const emailTest = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

        this.tfaSubscription = this.checkTfaSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                filter((email: string) => emailTest.test(email)),
                switchMap((username: string) =>
                    extendWithLoadingAndFail(
                        this.loginService.getTwoFactorMode(username).pipe(
                            catchError((error: any) => {
                                this.requiresTwoFactorAuth = false;
                                return observableOf<IResponse<TwoFactorAuthMode>>(
                                    error as IResponse<TwoFactorAuthMode>,
                                );
                            }),
                        ),
                    ),
                ),
            )
            .subscribe(
                (response) => {
                    this.tfaResponse = response;
                    this.requiresTwoFactorAuth = false;

                    if (response && response.data) {
                        const userAuthMode: TwoFactorAuthMode = response.data;
                        if (userAuthMode === TwoFactorAuthMode.GoogleAuthenticator) {
                            this.requiresTwoFactorAuth = true;
                            this.$scope.$applyAsync();
                        }
                    }
                },
                (response) => {
                    this.tfaResponse = response;
                    this.requiresTwoFactorAuth = false;
                    this.$scope.$applyAsync();
                },
            );

        this.checkPreUserName();

        this.loginService.isLoggedInObservable().subscribe((loggedIn) => {
            this.isLoggedIn = loggedIn;
        });
        this.profileService.profileObservable().subscribe((profile) => {
            this.profile = profile;
        });
    }

    public checkTwoFactorAuth() {
        return this.$timeout(() => {
            if (this.login && this.login.username && this.login.username.length > 0) {
                this.checkTfaSubject.next(this.login.username);
            }
        });
    }

    public submitForm(): void {
        this.beforeRedirect = true;

        extendWithLoadingAndFail(this.loginService.login(this.login))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.loginResponse = response;

                    if (response.status === 401) {
                        this.modalService.open<null, null>("irUnauthorizedModal", null, null).subscribe(() => noop());
                    }

                    if (response.data && response.status === 200) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        this.stateService.go(this.stateAfterLogin);
                    }
                },
                (response) => {
                    this.loginResponse = response;
                    this.beforeRedirect = false;
                },
            );
    }

    public logout(): void {
        this.loginService.logout();
    }

    public checkPreUserName(): void {
        if (this.login && this.login.username) {
            this.checkTwoFactorAuth();
        }
    }
}
